import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Calendar, 
  User, 
  ArrowRight, 
  Tag,
  Clock,
  Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Sample blog posts (will be replaced with API data once seeded)
  const samplePosts = [
    {
      id: '1',
      title: 'Understanding Life Insurance: A Complete Guide for Canadian Families',
      slug: 'understanding-life-insurance-guide',
      excerpt: 'Life insurance is one of the most important financial decisions you can make. Learn about the different types of coverage and how to choose the right policy for your family.',
      content: '',
      author: 'Justin Castillo',
      category: 'Insurance',
      image_url: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800',
      published: true,
      created_at: '2025-02-15T10:00:00Z'
    },
    {
      id: '2',
      title: 'First-Time Home Buyer Tips: Navigating the Canadian Market in 2025',
      slug: 'first-time-home-buyer-tips-2025',
      excerpt: 'Buying your first home is exciting but can be overwhelming. Here are essential tips to help you navigate the process and secure the best mortgage rates.',
      content: '',
      author: 'Linda Mallari',
      category: 'Mortgage',
      image_url: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800',
      published: true,
      created_at: '2025-02-10T10:00:00Z'
    },
    {
      id: '3',
      title: 'RRSP vs TFSA: Which Investment Account is Right for You?',
      slug: 'rrsp-vs-tfsa-comparison',
      excerpt: 'Both RRSPs and TFSAs offer tax advantages, but they work differently. Learn which account suits your financial goals and how to maximize your savings.',
      content: '',
      author: 'Norman Agustin',
      category: 'Investment',
      image_url: 'https://images.unsplash.com/photo-1579621970795-87facc2f976d?w=800',
      published: true,
      created_at: '2025-02-05T10:00:00Z'
    },
    {
      id: '4',
      title: 'Critical Illness Insurance: Why Every Canadian Should Consider It',
      slug: 'critical-illness-insurance-importance',
      excerpt: 'A serious illness can impact your finances significantly. Discover how critical illness insurance can provide financial security when you need it most.',
      content: '',
      author: 'Justin Castillo',
      category: 'Insurance',
      image_url: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800',
      published: true,
      created_at: '2025-01-28T10:00:00Z'
    },
    {
      id: '5',
      title: 'Mortgage Refinancing: When Does It Make Sense?',
      slug: 'mortgage-refinancing-guide',
      excerpt: 'Refinancing your mortgage can save you money or help you access home equity. Learn about the best scenarios for refinancing and what to consider.',
      content: '',
      author: 'Linda Mallari',
      category: 'Mortgage',
      image_url: 'https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?w=800',
      published: true,
      created_at: '2025-01-20T10:00:00Z'
    },
    {
      id: '6',
      title: 'Building an Emergency Fund: Your Financial Safety Net',
      slug: 'building-emergency-fund',
      excerpt: 'An emergency fund is crucial for financial stability. Learn how much you should save and the best strategies to build your safety net.',
      content: '',
      author: 'Norman Agustin',
      category: 'Investment',
      image_url: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800',
      published: true,
      created_at: '2025-01-15T10:00:00Z'
    }
  ];

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog`);
      if (response.ok) {
        const data = await response.json();
        // If no posts from API, use sample posts
        setPosts(data.length > 0 ? data : samplePosts);
      } else {
        setPosts(samplePosts);
      }
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      setPosts(samplePosts);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', 'Insurance', 'Mortgage', 'Investment'];

  const filteredPosts = posts.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Insurance': 'bg-blue-100 text-blue-700',
      'Mortgage': 'bg-green-100 text-green-700',
      'Investment': 'bg-purple-100 text-purple-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[350px] flex items-center justify-center overflow-hidden bg-[#0077B6]">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0077B6] to-[#005A8C]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">Financial Insights Blog</h1>
          <p className="text-lg text-blue-100">
            Expert advice and insights on insurance, mortgages, and investments
          </p>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-8 bg-[#F0F7FF] border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div className="flex gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-[#0077B6] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {category === 'all' ? 'All Posts' : category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-[#0077B6] border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-500">Loading articles...</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No articles found matching your criteria.</p>
              <Button 
                onClick={() => { setSelectedCategory('all'); setSearchQuery(''); }}
                className="mt-4 bg-[#0077B6]"
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Card key={post.id} className="overflow-hidden hover:shadow-xl transition-shadow group">
                  {post.image_url && (
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={post.image_url} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-3 left-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(post.category)}`}>
                          {post.category}
                        </span>
                      </div>
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                      <span className="flex items-center">
                        <Calendar className="h-3.5 w-3.5 mr-1" />
                        {formatDate(post.created_at)}
                      </span>
                      <span className="flex items-center">
                        <User className="h-3.5 w-3.5 mr-1" />
                        {post.author}
                      </span>
                    </div>
                    <h2 className="text-xl font-bold text-gray-800 mb-2 line-clamp-2 group-hover:text-[#0077B6] transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center text-[#0077B6] font-medium text-sm hover:text-[#005A8C] transition-colors"
                    >
                      Read More
                      <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-12 bg-[#003D5C]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Stay Informed</h2>
          <p className="text-blue-200 mb-6">
            Contact us to learn more about our financial services and get personalized advice.
          </p>
          <Button 
            asChild
            size="lg"
            className="bg-[#D4AF37] text-[#003D5C] hover:bg-[#B8962E] font-bold"
          >
            <Link to="/contact">
              Contact Our Team
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
